/**
 * Chart Block Renderer
 * Renders various chart types for data visualization
 */

import React from "react";
import {
  LineChart,
  BarChart,
  AreaChart,
  PieChart,
  RadialChart,
  ScatterChart
} from "@strivelabs/ui-blocks";
import type { ChartReferenceLine, ChartReferenceDot, ChartReferenceArea } from "@strivelabs/ui-blocks";
import type { UIBlock } from "@/types/ui-blocks";

function pickReferenceProps(content: { referenceLines?: unknown[]; referenceDots?: unknown[]; referenceAreas?: unknown[] }) {
  return {
    referenceLines: content.referenceLines as ChartReferenceLine[] | undefined,
    referenceDots: content.referenceDots as ChartReferenceDot[] | undefined,
    referenceAreas: content.referenceAreas as ChartReferenceArea[] | undefined,
  };
}

export interface ChartBlockRendererProps {
  block: UIBlock;
}

export function ChartBlockRenderer({ block }: ChartBlockRendererProps) {
  if (block.type === 'line_chart') {
    const content = block.content;
    const refProps = pickReferenceProps(content);
    return (
      <LineChart
        data={content.data}
        xAxisKey={content.xAxisKey}
        config={content.config}
        lines={content.lines}
        title={content.title}
        description={content.description}
        showGrid={content.showGrid}
        showTooltip={content.showTooltip}
        showLegend={content.showLegend}
        height={content.height}
        {...refProps}
      />
    );
  }

  if (block.type === 'bar_chart') {
    const content = block.content;
    const refProps = pickReferenceProps(content);
    return (
      <BarChart
        data={content.data}
        xAxisKey={content.xAxisKey}
        title={content.title}
        dataKeys={content.dataKeys}
        height={content.height}
        showGrid={content.showGrid ?? true}
        showLegend={content.showLegend ?? true}
        className={content.className ?? ''}
        {...refProps}
      />
    );
  }

  if (block.type === 'area_chart') {
    const content = block.content;
    const refProps = pickReferenceProps(content);
    return (
      <AreaChart
        data={content.data}
        xAxisKey={content.xAxisKey}
        config={content.config}
        areas={content.areas}
        title={content.title}
        description={content.description}
        showGrid={content.showGrid}
        showTooltip={content.showTooltip}
        showLegend={content.showLegend}
        height={content.height}
        {...refProps}
      />
    );
  }

  if (block.type === 'pie_chart') {
    const content = block.content;
    return (
      <PieChart
        data={content.data}
        dataKey={content.dataKey}
        nameKey={content.nameKey}
        config={content.config}
        title={content.title}
        description={content.description}
        showTooltip={content.showTooltip}
        showLegend={content.showLegend}
        height={content.height}
        innerRadius={content.innerRadius}
        outerRadius={content.outerRadius}
        paddingAngle={content.paddingAngle}
      />
    );
  }

  if (block.type === 'radial_chart') {
    const content = block.content;
    return (
      <RadialChart
        data={content.data}
        dataKey={content.dataKey}
        nameKey={content.nameKey}
        config={content.config}
        title={content.title}
        description={content.description}
        showTooltip={content.showTooltip}
        showLegend={content.showLegend}
        height={content.height}
        innerRadius={content.innerRadius}
        outerRadius={content.outerRadius}
        startAngle={content.startAngle}
        endAngle={content.endAngle}
      />
    );
  }

  if (block.type === 'scatter_chart') {
    const content = block.content;
    return (
      <ScatterChart
        data={content.data}
        xAxisKey={content.xAxisKey}
        yAxisKey={content.yAxisKey}
        scatters={content.scatters}
        config={content.config}
        title={content.title}
        description={content.description}
        showGrid={content.showGrid}
        showTooltip={content.showTooltip}
        showLegend={content.showLegend}
        height={content.height}
        xAxisLabel={content.xAxisLabel}
        yAxisLabel={content.yAxisLabel}
      />
    );
  }

  return null;
}
